import { useMemo, useState } from 'react';
import styled from '@emotion/styled';
import { StyledInput, StyledText } from '@selfserve/react-ui';
import { FormikProps } from 'formik';
import { useTranslation } from 'react-i18next';
import { Visible24Icon, Invisible24Icon } from '@kep-ui-kit/ui-icons';

import {
  StyledInputFieldContainer,
  FormData,
} from '@selfserve/react-feature-create-account';
import { isTabEvent, submitByKeyboard } from '@selfserve/react-utils';

const StyledPasswordFieldsContainer = styled('div')(() => ({
  svg: {
    color: '#888888',
    cursor: 'pointer',
    margin: '0 8px',
  },
}));

const StyledPasswordInput = styled(StyledInput, {
  shouldForwardProp: (prop) => {
    return prop !== 'hideText';
  },
})(
  ({ hideText }: { hideText?: boolean }) =>
    hideText &&
    `
      -webkit-text-security: disc;
      -moz-text-security: disc;
      text-security: disc;
    `
);

const StyledShowHideBtn = styled('button')(() => ({
  background: 'none',
  border: 'none',
  margin: 0,
  padding: '0',
  outlineColor: 'rgb(0, 110, 250)',
}));

const StyledErrTextWrapper = styled.div(
  ({
    theme,
    hasError,
    touched,
  }: {
    theme?: any;
    hasError?: boolean;
    touched?: boolean;
  }) => ({
    display: 'flex',
    justifyContent: 'flex-start',
    lineHeight: theme.typography.pxToRem(17),
    alignItems: 'center',
    flexDirection: 'row',

    p: {
      fontSize: '16px',
      ...(touched && hasError
        ? {
          color: theme.palette.danger.main,
        }
        : {
          color: '#333',
        }),
    },
  })
);

const StyledErrFieldIcon = styled.img(
  ({
    theme,
    hasError,
    touched,
  }: {
    theme?: any;
    hasError?: boolean;
    touched?: boolean;
  }) => ({
    maxWidth: '8px',
    maxHeight: '8px',
    padding: '4px',
    backgroundColor: '#CCCCCC',
    marginRight: theme.spacing(1),
    borderRadius: '50%',
    marginTop: '8px',
    ...(touched && hasError
      ? {
        padding: '3px',
        backgroundColor: '#E12437',
        maxWidth: '20px',
        maxHeight: '20px',
      }
      : touched && {
        backgroundColor: '#00A078',
        padding: '3px',
        maxWidth: '20px',
        maxHeight: '20px',
      }),
  })
);

StyledErrFieldIcon.defaultProps = {
  src: '/assets/circle.svg',
};

interface PasswordRequirementProps {
  hasError: boolean;
  errText: string;
  touched: boolean;
}

export const PasswordRequirement = ({
  hasError,
  errText,
  touched,
}: PasswordRequirementProps) => {
  const iconUrl = !touched
    ? '/assets/circle.svg'
    : hasError
      ? '/assets/fail.svg'
      : '/assets/check.svg';

  return (
    <StyledErrTextWrapper
      hasError={hasError}
      touched={touched}
      className={`password-checking ${!hasError ? 'checked' : ''}`}
    >
      <StyledErrFieldIcon src={iconUrl} hasError={hasError} touched={touched} />
      <StyledText>{errText}</StyledText>
    </StyledErrTextWrapper>
  );
};

const CommonPasswordShowHideIcon = (
  passwordVisible: boolean,
  setPasswordVisibility: React.Dispatch<React.SetStateAction<boolean>>
) => {
  const { t } = useTranslation();
  return (
    <StyledShowHideBtn
      type="button"
      onClick={() => setPasswordVisibility((prevState) => !prevState)}
      tabIndex={0}
      onKeyDown={(event) => {
        if (!submitByKeyboard(event) && !isTabEvent) event.preventDefault();
        !isTabEvent && setPasswordVisibility((prevState) => !prevState);
      }}
      aria-label={
        passwordVisible ? t('aria.hide-password') : t('aria.show-password')
      }
      id={
        passwordVisible
          ? 'kpfCreateAccountHidePassword'
          : 'kpfCreateAccountShowPassword'
      }
    >
      {passwordVisible ? (
        <Invisible24Icon aria-label={t('aria.show-password')} />
      ) : (
        <Visible24Icon aria-label={t('aria.hide-password')} />
      )}
    </StyledShowHideBtn>
  );
};

interface Props {
  formik: FormikProps<FormData>;
  hidePasswordError?: boolean;
  autofocus?: boolean;
  unbold?: boolean;
  withoutConfirmation?: boolean;
}

export const PasswordFields = ({
  formik: { touched, handleChange, handleBlur, values, errors, submitCount },
  hidePasswordError,
  autofocus,
  unbold,
  withoutConfirmation
}: Props) => {
  const { t } = useTranslation();
  const { passwordErrors } = errors;
  const [passwordFieldVisible, setPasswordFieldVisible] = useState(false);
  const [confrimPasswordFieldVisible, setConfirmPasswordFieldVisible] =
    useState(false);
  const isTouched = useMemo(
    () =>
      (!!values.password || !!errors.password) &&
      (!!touched.password || !!submitCount),
    [values, errors, touched, submitCount]
  );

  return (
    <StyledPasswordFieldsContainer>
      <StyledInputFieldContainer
        className={`password-checking-container ${touched.password ? 'touched' : ''}`}
        unbold={unbold}
      >
        <StyledPasswordInput
          id="password"
          name="password"
          inputLabel={t('create-account.password')}
          autoFocus={autofocus}
          onChange={handleChange}
          onBlur={handleBlur}
          value={values.password}
          error={!!touched.password && !!errors.password}
          formHelperText={
            touched.password && !hidePasswordError ? errors.password : undefined
          }
          endAdornment={CommonPasswordShowHideIcon(
            passwordFieldVisible,
            setPasswordFieldVisible
          )}
          inputProps={{ 'aria-label': t('aria.password') }}
          autoComplete="password"
          hideText={!passwordFieldVisible}
        />
        <PasswordRequirement
          errText={t('common.validations.at-list-eight-character')}
          touched={isTouched}
          hasError={!values.password || !!passwordErrors?.minLengthErr}
        />
        <PasswordRequirement
          errText={t('common.validations.at-list-one-number')}
          touched={isTouched}
          hasError={!values.password || !!passwordErrors?.oneNumberErr}
        />
        <PasswordRequirement
          errText={t('common.validations.at-list-one-uppercase-character')}
          touched={isTouched}
          hasError={!values.password || !!passwordErrors?.upperCaseErr}
        />
        <PasswordRequirement
          errText={t('common.validations.at-list-one-lowercase-character')}
          touched={isTouched}
          hasError={!values.password || !!passwordErrors?.lowerCaseErr}
        />
      </StyledInputFieldContainer>
      { !withoutConfirmation && <StyledInputFieldContainer unbold={unbold}>
        <StyledPasswordInput
          id="confirmPassword"
          name="confirmPassword"
          inputLabel={t('create-account.confirm-password')}
          onChange={handleChange}
          onBlur={handleBlur}
          value={values.confirmPassword}
          error={!!touched.confirmPassword && !!errors.confirmPassword}
          formHelperText={
            touched.confirmPassword ? errors.confirmPassword : undefined
          }
          endAdornment={CommonPasswordShowHideIcon(
            confrimPasswordFieldVisible,
            setConfirmPasswordFieldVisible
          )}
          inputProps={{ 'aria-label': t('aria.confirm-password') }}
          autoComplete="confirmPassword"
          hideText={!confrimPasswordFieldVisible}
        />
      </StyledInputFieldContainer> }
    </StyledPasswordFieldsContainer>
  );
};
